// src/features/stamp/stampSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit"
import { getPublicStampApi } from "@core/api/hospitalStampApi"
import { getMyStampImage, normalizeMemberStampImage } from "@core/api/memberApi"

interface StampState {
  personalStampImage: string | null // 의사 개인 도장
  publicStampImage: string | null // 병원 공식 도장
  stampName: string | null
  loading: boolean
  error: string | null
}

const initialState: StampState = {
  personalStampImage: null,
  publicStampImage: null,
  stampName: null,
  loading: false,
  error: null,
}

// 공개 도장 이미지 조회 Thunk
export const fetchPublicStamp = createAsyncThunk("stamp/fetchPublicStamp", async (_, { rejectWithValue }) => {
  try {
    const response = await getPublicStampApi()
    return {
      stampImage: response.stampImage || null,
      stampName: response.stampName || null,
    }
  } catch (error: any) {
    // 404나 403은 정상 (도장 없음 또는 사용 불가)
    if (error?.response?.status === 404 || error?.response?.status === 403) {
      return {
        stampImage: null,
        stampName: null,
      }
    }
    return rejectWithValue(error.message || "도장 이미지 조회 실패")
  }
})

// 개인 도장 이미지 조회 Thunk
export const fetchMyStamp = createAsyncThunk("stamp/fetchMyStamp", async (_, { rejectWithValue }) => {
  try {
    const response = await getMyStampImage()
    const stampImage = normalizeMemberStampImage(response?.stampImage, response?.mimeType)

    return {
      stampImage,
      stampName: null,
    }
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return {
        stampImage: null,
        stampName: null,
      }
    }
    return rejectWithValue(error.message || "도장 이미지 조회 실패")
  }
})

const stampSlice = createSlice({
  name: "stamp",
  initialState,
  reducers: {
    clearStamp: state => {
      state.personalStampImage = null
      state.publicStampImage = null
      state.stampName = null
      state.error = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPublicStamp.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPublicStamp.fulfilled, (state, action: PayloadAction<{ stampImage: string | null; stampName: string | null }>) => {
        state.loading = false
        state.publicStampImage = action.payload.stampImage
        state.stampName = action.payload.stampName
      })
      .addCase(fetchPublicStamp.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(fetchMyStamp.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMyStamp.fulfilled, (state, action: PayloadAction<{ stampImage: string | null; stampName: string | null }>) => {
        state.loading = false
        state.personalStampImage = action.payload.stampImage
        state.stampName = action.payload.stampName
      })
      .addCase(fetchMyStamp.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

export const { clearStamp } = stampSlice.actions
export default stampSlice.reducer
